import Table from "common/Table/Table";
import {
  BacktestingRunType,
  BacktestingSort,
  BacktestingStatus,
  BotType,
  LanguageNameSpaces,
  SortingOrder,
} from "@shared/types";
import { TableLoadingEmpty } from "common/styles";
import React, { ReactNode } from "react";
import { ColumnsType } from "antd/es/table";
import Text from "common/Typography/Text";
import { getDisplayedTimeframe } from "@shared/features/bots/utils";
import BacktestingActions from "features/bots/BotForm/Backtesting/common/BacktestingActions";
import BacktestStatusTag from "features/bots/BotForm/Backtesting/common/BacktestStatusTag";
import { formatDate } from "@shared/utils/date";
import PnlComponent from "features/bots/BotForm/Backtesting/common/PnlComponent";
import { BacktestingGeneralStatistics } from "@shared/features/bots/BotForm/common/types";
import TextWithIcon from "common/Text/TextWithIcon";
import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import { typography } from "@shared/common/Themes/typography";
import { SORT_ORDER_MAPPING } from "common/constants";
import { getTableOnChangeFunction } from "common/Table/utils";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import Card from "common/Boxes/Card";
import useMedia from "utils/hooks/useMedia";
import { useNavigateToBacktestDetails } from "features/bots/common/hooks";
import { FAILED_BACKTESTING_EXTRA_DETAILS_MESSAGE } from "@shared/features/bots/BotForm/common/constants";
import { openMessage } from "utils/misc";
import { getLiveBacktestingStatus } from "@shared/features/bots/BotForm/Backtesting/utils";
import Tooltip from "common/Tooltips/Tooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface BacktestingHistoryTableProps {
  botType: BotType;
  data?: BacktestingRunType[];
  openDeleteModal?: (backtesting: BacktestingRunType) => void;
  emptyState?: ReactNode;
  sortBy?: BacktestingSort;
  setSortBy: (sortBy: BacktestingSort) => void;
  sortDirection: SortingOrder;
  setSortDirection: (sortDirection: SortingOrder) => void;
  isFetching?: boolean;
}

const BacktestingHistoryTable = ({
  botType,
  data,
  openDeleteModal,
  emptyState,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  isFetching,
}: BacktestingHistoryTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  let displayCurrency = useSelector(selectDisplayCurrency);
  const maxColumnWidth = useMedia(
    [`(max-width: 900px)`, `(max-width: 1400px)`],
    [150, 300],
    undefined
  );
  const { navigateToBacktestDetails } = useNavigateToBacktestDetails();

  let columns: ColumnsType<BacktestingRunType> = [];

  if (botType === BotType.SIGNAL) {
    columns.push({
      title: t("backtesting.labels.group"),
      dataIndex: "sub_group_name",
      showSorterTooltip: false,
      width: maxColumnWidth,
      sorter: true,
      sortOrder:
        sortBy === BacktestingSort.GROUP
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, backtestingRun) => (
        <Tooltip title={backtestingRun.sub_group_name}>
          <Flex
            $justifyContent="flex-start"
            style={{ maxWidth: maxColumnWidth, width: "fit-content" }}
          >
            <IconWithFallback
              iconLink={backtestingRun.sub_group_image}
              coinOrExchangeName={backtestingRun.sub_group_name}
              pxIconSize={32}
              iconStyle={{ borderRadius: 6, marginRight: 12 }}
            />
            <Text $ellipsis={true}>{backtestingRun.sub_group_name}</Text>
          </Flex>
        </Tooltip>
      ),
    });
  } else if (botType === BotType.ASAP) {
    columns.push({
      title: t("backtesting.labels.symbol"),
      dataIndex: "symbol",
      showSorterTooltip: false,
      sorter: true,
      sortOrder:
        sortBy === BacktestingSort.SYMBOL
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (symbol) => (
        <TextWithIcon
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={symbol}
          pxIconSize={32}
          text={symbol}
          iconStyle={{ marginRight: 12 }}
        />
      ),
    });
  }

  columns = [
    ...columns,
    {
      title: t("backtesting.labels.name"),
      dataIndex: "name",
      showSorterTooltip: false,
      sorter: true,
      sortOrder:
        sortBy === BacktestingSort.NAME
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, backtestingRun) => (
        <TextWithIcon
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={backtestingRun.exchange}
          pxIconSize={24}
          iconStyle={{ marginRight: 12 }}
          text={
            <div>
              <Text>{backtestingRun.name}</Text>
              <Text type="secondary" $size={typography.fontSizes.S}>
                {backtestingRun.client_name}
              </Text>
            </div>
          }
        />
      ),
    },
    {
      title: t("backtesting.labels.created_at"),
      dataIndex: "created_at",
      showSorterTooltip: false,
      sorter: true,
      sortOrder:
        sortBy === BacktestingSort.CREATED
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (createdAt) => <Text>{formatDate(createdAt, true, true)}</Text>,
    },
    {
      title: t("backtesting.labels.net_pnl"),
      dataIndex: `net_pnl.${displayCurrency}`,
      showSorterTooltip: false,
      sorter: true,
      sortOrder:
        sortBy === BacktestingSort.NET_PNL
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, backtestingRun) => (
        <PnlComponent
          pnlType={BacktestingGeneralStatistics.NET_PNL}
          backtestingRun={backtestingRun}
        />
      ),
    },
    {
      title: t("backtesting.labels.timeframe"),
      dataIndex: "backtesting_time_range.end",
      showSorterTooltip: false,
      sorter: true,
      sortOrder:
        sortBy === BacktestingSort.TIME_FRAME
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, backtestingRun) => (
        <Text>
          {getDisplayedTimeframe(backtestingRun.backtesting_time_range)}
        </Text>
      ),
    },
    {
      title: t("backtesting.labels.status"),
      dataIndex: "status",
      showSorterTooltip: false,
      sorter: true,
      sortOrder:
        sortBy === BacktestingSort.STATUS
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, backtestingRun) => (
        <BacktestStatusTag backtestingRun={backtestingRun} />
      ),
    },
    {
      title: "",
      showSorterTooltip: false,
      render: (_, backtestingRun) => (
        <BacktestingActions
          backtestingRun={backtestingRun}
          openDeleteModal={() => openDeleteModal?.(backtestingRun)}
          botType={botType}
        />
      ),
    },
  ];

  const FIELD_NAME_TO_SORT = {
    sub_group_name: BacktestingSort.GROUP,
    name: BacktestingSort.NAME,
    [`net_pnl.${displayCurrency}`]: BacktestingSort.NET_PNL,
    status: BacktestingSort.STATUS,
    created_at: BacktestingSort.CREATED,
    ["backtesting_time_range.end"]: BacktestingSort.TIME_FRAME,
  };

  const handleChange = getTableOnChangeFunction<BacktestingSort>(
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    FIELD_NAME_TO_SORT
  );

  const openExtraDetailsDrawer = (backtestData: BacktestingRunType) => {
    if (!data) return;
    const liveStatus = getLiveBacktestingStatus(backtestData);
    if (liveStatus === BacktestingStatus.FAILED) {
      openMessage(t(FAILED_BACKTESTING_EXTRA_DETAILS_MESSAGE), "warning");
    } else {
      navigateToBacktestDetails(backtestData.bot_id, backtestData.id);
    }
  };

  return (
    <Card>
      <Table<BacktestingRunType>
        columns={columns}
        dataSource={data}
        loading={isFetching}
        onChange={handleChange}
        rowClassName="u-cursorPointer"
        rowKey={(backtesting) => backtesting.id}
        locale={{ emptyText: emptyState || <TableLoadingEmpty /> }}
        onRow={(backtestData) => ({
          onClick: () => openExtraDetailsDrawer(backtestData),
        })}
      />
    </Card>
  );
};

export default BacktestingHistoryTable;
